import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import { downloadCsv } from './adminCsvUtils';
import '../../styles/AdminDashboard.css';

export default function AdminApplicationsPage() {
  const { setRefreshHandler } = useOutletContext();

  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationsError, setApplicationsError] = useState('');
  const [applicationsPagination, setApplicationsPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [applicationsFilters, setApplicationsFilters] = useState({ status: '', q: '' });
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationNotesDraft, setApplicationNotesDraft] = useState('');
  const [applicationStatusDraft, setApplicationStatusDraft] = useState('');
  const [applicationsSaving, setApplicationsSaving] = useState(false);

  const getApplicationSourceLabel = useCallback((app) => {
    if (!app) return '';
    const whyJoin = String(app.whyJoin || '').trim();
    const valueBring = String(app.valueBring || '').trim();
    if (whyJoin === 'Invited to join via /memberships' || valueBring.startsWith('Invitation acceptance')) {
      return 'Membership invitation';
    }
    if (app.source === 'luxury_landing_page') return 'Landing page';
    return app.source || '';
  }, []);

  const fetchApplications = useCallback(async (opts = {}) => {
    try {
      setApplicationsLoading(true);
      setApplicationsError('');

      const page = opts.page || applicationsPagination.page || 1;
      const limit = applicationsPagination.limit || 25;

      const result = await adminApi.listApplications({
        page,
        limit,
        status: applicationsFilters.status || undefined,
        q: applicationsFilters.q || undefined
      });

      setApplications(result.data || []);
      setApplicationsPagination(result.pagination || { page, limit, total: 0, pages: 0 });
    } catch (e) {
      setApplicationsError('Failed to load applications');
    } finally {
      setApplicationsLoading(false);
    }
  }, [applicationsFilters.q, applicationsFilters.status, applicationsPagination.limit, applicationsPagination.page]);

  useEffect(() => {
    fetchApplications({ page: 1 });
  }, [fetchApplications]);

  useEffect(() => {
    setRefreshHandler(() => fetchApplications({ page: applicationsPagination.page }));
  }, [setRefreshHandler, fetchApplications, applicationsPagination.page]);

  return (
    <div className="admin-dashboard">
<div className="analytics-section">
            <div className="admin-table-header">
              <div>
                <h2>Membership Applications</h2>
                <p className="admin-table-subtitle">Review, update status, add internal notes, and export CSV</p>
              </div>
              <div className="admin-table-actions">
                <button
                  className="btn"
                  disabled={applicationsLoading}
                  onClick={() => fetchApplications({ page: applicationsPagination.page })}
                >
                  Refresh
                </button>
                <button
                  className="btn"
                  disabled={applicationsLoading}
                  onClick={async () => {
                    try {
                      const csv = await adminApi.exportApprovedMailingListCsv();
                      downloadCsv('approved-mailing-list.csv', csv);
                    } catch (e) {
                      setApplicationsError('Failed to export CSV');
                    }
                  }}
                >
                  Export Approved Mailing List
                </button>
              </div>
            </div>

            <div className="admin-table-filters">
              <input
                type="text"
                placeholder="Search name/email/company/role/source..."
                value={applicationsFilters.q}
                onChange={(e) => setApplicationsFilters((p) => ({ ...p, q: e.target.value }))}
              />
              <select
                value={applicationsFilters.status}
                onChange={(e) => setApplicationsFilters((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="">All statuses</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="waitlisted">Waitlisted</option>
                <option value="reviewing">Reviewing (legacy)</option>
                <option value="accepted">Accepted (legacy)</option>
                <option value="rejected">Rejected (legacy)</option>
              </select>
              <button
                className="btn"
                disabled={applicationsLoading}
                onClick={() => fetchApplications({ page: 1 })}
              >
                Apply
              </button>
            </div>

            {applicationsError ? <div className="error-message">{applicationsError}</div> : null}
            {applicationsLoading ? <div className="loading">Loading applications…</div> : null}

            <div className="admin-table">
              <table>
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Role/Title</th>
                    <th>Status</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length === 0 && !applicationsLoading ? (
                    <tr>
                      <td colSpan={7} className="adminMutedText" style={{ textAlign: 'center', padding: '1.5rem' }}>
                        No applications found
                      </td>
                    </tr>
                  ) : null}
                  {applications.map((app) => (
                    <tr
                      key={app._id}
                      className="admin-row-clickable"
                      onClick={() => {
                        setSelectedApplication(app);
                        setApplicationNotesDraft(app.notes || '');
                        setApplicationStatusDraft(app.status || 'new');
                      }}
                    >
                      <td>{app.createdAt ? new Date(app.createdAt).toLocaleString() : ''}</td>
                      <td>{app.name}</td>
                      <td>{app.email}</td>
                      <td>{app.company || ''}</td>
                      <td>{app.roleTitle || ''}</td>
                      <td>
                        <span className={`status-pill status-${String(app.status || 'new').toLowerCase()}`}>
                          {app.status}
                        </span>
                      </td>
                      <td>{getApplicationSourceLabel(app)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-table-pagination">
              <button
                className="btn"
                disabled={applicationsLoading || applicationsPagination.page <= 1}
                onClick={() => fetchApplications({ page: applicationsPagination.page - 1 })}
              >
                Prev
              </button>
              <span className="adminMutedText">
                Page {applicationsPagination.page} / {applicationsPagination.pages || 1} (Total {applicationsPagination.total})
              </span>
              <button
                className="btn"
                disabled={applicationsLoading || applicationsPagination.page >= (applicationsPagination.pages || 1)}
                onClick={() => fetchApplications({ page: applicationsPagination.page + 1 })}
              >
                Next
              </button>
            </div>

            {selectedApplication ? (
              <div className="admin-drawer-overlay" onClick={() => setSelectedApplication(null)}>
                <div className="admin-drawer" onClick={(e) => e.stopPropagation()}>
                  <div className="admin-drawer-header">
                    <h3>Application Detail</h3>
                    <button className="admin-drawer-close" onClick={() => setSelectedApplication(null)}>×</button>
                  </div>

                  <div className="admin-drawer-body">
                    <div className="admin-field">
                      <div className="admin-field-label">Applicant</div>
                      <div className="admin-field-value">{selectedApplication.name} — {selectedApplication.email}</div>
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Company</div>
                      <div className="admin-field-value">{selectedApplication.company || ''}</div>
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Role / Title</div>
                      <div className="admin-field-value">{selectedApplication.roleTitle || ''}</div>
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Submitted</div>
                      <div className="admin-field-value">{selectedApplication.createdAt ? new Date(selectedApplication.createdAt).toLocaleString() : ''}</div>
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Status</div>
                      <select value={applicationStatusDraft} onChange={(e) => setApplicationStatusDraft(e.target.value)}>
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                          <option value="approved">Approved</option>
                          <option value="denied">Denied</option>
                          <option value="waitlisted">Waitlisted</option>
                        <option value="reviewing">Reviewing (legacy)</option>
                          <option value="accepted">Accepted (legacy)</option>
                          <option value="rejected">Rejected (legacy)</option>
                      </select>
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Notes (internal)</div>
                      <textarea
                        rows={6}
                        value={applicationNotesDraft}
                        onChange={(e) => setApplicationNotesDraft(e.target.value)}
                        placeholder="Add internal notes…"
                      />
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Why join</div>
                      <div className="admin-field-pre">{selectedApplication.whyJoin || ''}</div>
                    </div>

                    <div className="admin-field">
                      <div className="admin-field-label">Value bring</div>
                      <div className="admin-field-pre">{selectedApplication.valueBring || ''}</div>
                    </div>
                  </div>

                  <div className="admin-drawer-footer">
                    <button
                      className="btn"
                      disabled={applicationsSaving}
                      onClick={() => setApplicationStatusDraft('approved')}
                    >
                      Approve
                    </button>
                    <button
                      className="btn"
                      disabled={applicationsSaving}
                      onClick={() => setApplicationStatusDraft('waitlisted')}
                    >
                      Waitlist
                    </button>
                    <button
                      className="btn"
                      disabled={applicationsSaving}
                      onClick={() => setApplicationStatusDraft('denied')}
                    >
                      Deny
                    </button>
                    <button
                      className="btn"
                      disabled={applicationsSaving}
                      onClick={() => setApplicationStatusDraft('reviewed')}
                    >
                      Mark Reviewed
                    </button>
                    <button
                      className="btn"
                      disabled={applicationsSaving}
                      onClick={async () => {
                        try {
                          setApplicationsSaving(true);
                          const updated = await adminApi.updateApplicationStatus(selectedApplication._id, {
                            status: applicationStatusDraft,
                            notes: applicationNotesDraft
                          });
                          const updatedApp = updated.data;
                          setSelectedApplication(updatedApp);
                          setApplications((prev) => prev.map((a) => a._id === updatedApp._id ? updatedApp : a));
                        } catch (e) {
                          setApplicationsError('Failed to save application');
                        } finally {
                          setApplicationsSaving(false);
                        }
                      }}
                    >
                      Save
                    </button>
                    <button className="btn" onClick={() => setSelectedApplication(null)} disabled={applicationsSaving}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
    </div>
  );
}
